import { EditorState, $nodesOfType } from 'lexical';
import { PromptItemNode, PromptItemType } from '../components/editor/prompt-input/PromptItemNode';

// ============================================
// Type Definitions
// ============================================

export interface ElementContext {
  editId: string;
  tagName: string;
  compName?: string;
  outerHTML: string;
  parent?: { tagName: string; compName?: string };
  siblings?: { tagName: string; compName?: string }[];
  computedStyles?: Record<string, string>;
}

export interface ImageContext {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
}

export interface DocumentContext {
  filename: string;
  content?: string;
  mimeType?: string;
}

export interface CodeContext {
  language?: string;
  content: string;
  filename?: string;
}

export interface AssetContext {
  assetId: string;
  filename: string;
  mimeType: string;
  category: string;
  url?: string;
  metadata?: any;
}

export interface PromptItemData {
  type: PromptItemType;
  id: string;
  label: string;
  isTentative: boolean;
  metadata?: Record<string, any>;
  context?: ElementContext | ImageContext | DocumentContext | CodeContext | AssetContext;
}

// CSS properties to extract for element context
const RELEVANT_CSS_PROPERTIES = [
  'display',
  'position',
  'width',
  'height',
  'margin',
  'padding',
  'background-color',
  'background-image',
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'text-align',
  'border',
  'border-radius',
  'box-shadow',
  'opacity',
  'transform',
  'flex-direction',
  'justify-content',
  'align-items',
  'gap',
  'grid-template-columns',
  'grid-template-rows',
];

// ============================================
// Extraction Functions
// ============================================

/**
 * Extract all PromptItemNodes from a Lexical editor state
 */
export function extractPromptItemsFromEditorState(editorState: EditorState): PromptItemData[] {
  const items: PromptItemData[] = [];
  
  editorState.read(() => {
    const promptItemNodes = $nodesOfType(PromptItemNode);
    
    for (const node of promptItemNodes) {
      // Skip tentative items - they shouldn't be sent to the server
      if (node.__isTentative) {
        continue;
      }
      
      items.push({
        type: node.__itemType,
        id: node.__id,
        label: node.__label,
        isTentative: node.__isTentative,
        metadata: node.__metadata,
      });
    }
  });
  
  return items;
}

/**
 * Extract element context from an iframe by editId
 */
export function extractElementContext(
  editId: string,
  iframeRef: React.RefObject<HTMLIFrameElement>
): ElementContext | null {
  if (!iframeRef.current?.contentWindow?.document) {
    console.warn('[extractElementContext] Iframe not available');
    return null;
  }
  
  const doc = iframeRef.current.contentWindow.document;
  const element = doc.querySelector(`[data-edit-id="${editId}"]`) as HTMLElement;
  
  if (!element) {
    console.warn(`[extractElementContext] Element not found: ${editId}`);
    return null;
  }
  
  // Get basic element info
  const tagName = element.tagName.toLowerCase();
  const compName = element.getAttribute('comp-name') || undefined;
  
  // Clean the outerHTML (remove edit-mode attributes)
  const outerHTML = cleanElementHTML(element.outerHTML);
  
  // Get parent info
  const parentElement = element.parentElement;
  const parent = parentElement ? {
    tagName: parentElement.tagName.toLowerCase(),
    compName: parentElement.getAttribute('comp-name') || undefined,
  } : undefined;
  
  // Get siblings info (up to 2 before and 2 after)
  const siblings: { tagName: string; compName?: string }[] = [];
  if (parentElement) {
    const children = Array.from(parentElement.children);
    const index = children.indexOf(element);
    
    // Get up to 2 siblings before
    for (let i = Math.max(0, index - 2); i < index; i++) {
      const sibling = children[i] as HTMLElement;
      siblings.push({
        tagName: sibling.tagName.toLowerCase(),
        compName: sibling.getAttribute('comp-name') || undefined,
      });
    }
    
    // Get up to 2 siblings after
    for (let i = index + 1; i < Math.min(children.length, index + 3); i++) {
      const sibling = children[i] as HTMLElement;
      siblings.push({
        tagName: sibling.tagName.toLowerCase(),
        compName: sibling.getAttribute('comp-name') || undefined,
      });
    }
  }
  
  // Get computed styles
  const computedStyles = extractRelevantStyles(element, iframeRef.current.contentWindow);
  
  return {
    editId,
    tagName,
    compName,
    outerHTML,
    parent,
    siblings: siblings.length > 0 ? siblings : undefined,
    computedStyles,
  };
}

/**
 * Clean element HTML by removing edit-mode specific attributes
 */
function cleanElementHTML(html: string): string {
  // Use a temporary DOM element to parse and manipulate HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const element = tempDiv.firstElementChild as HTMLElement;
  if (element) {
    // Remove edit-mode attributes
    element.removeAttribute('data-edit-id');
    element.classList.remove('edit-selected');
    
    // Remove style attribute if it's empty after cleaning
    if (element.getAttribute('style')?.trim() === '') {
      element.removeAttribute('style');
    }
  }
  
  return tempDiv.innerHTML;
}

/**
 * Extract relevant CSS properties from an element
 */
function extractRelevantStyles(
  element: HTMLElement,
  window: Window
): Record<string, string> {
  const computedStyle = window.getComputedStyle(element);
  const styles: Record<string, string> = {};
  
  for (const prop of RELEVANT_CSS_PROPERTIES) {
    const value = computedStyle.getPropertyValue(prop);
    // Only include non-default values
    if (value && value !== 'none' && value !== 'normal' && value !== 'auto') {
      styles[prop] = value;
    }
  }
  
  return styles;
}

/**
 * Enrich prompt items with their context
 */
export function enrichPromptItemsWithContext(
  items: PromptItemData[],
  iframeRef: React.RefObject<HTMLIFrameElement>
): PromptItemData[] {
  return items.map(item => {
    if (item.type === 'element') {
      // Extract editId from the item id (format: "tentative-{editId}" or just "{editId}")
      const editId = item.metadata?.editId || item.id.replace('tentative-', '');
      const context = extractElementContext(editId, iframeRef);
      
      if (context) {
        return { ...item, context };
      }
    }
    
    // For other types (image, document, code), context extraction can be added later
    return item;
  });
}

/**
 * Format prompt items into a context string for the AI
 * This is a client-side preview; the server does the actual formatting
 */
export function formatPromptItemsPreview(items: PromptItemData[]): string {
  if (items.length === 0) {
    return '';
  }
  
  const parts: string[] = [];
  
  for (const item of items) {
    if (item.type === 'element' && item.context) {
      const ctx = item.context as ElementContext;
      parts.push(`[Element: ${item.label}]`);
      if (ctx.compName) {
        parts.push(`  Comp: ${ctx.compName}`);
      }
      if (ctx.tagName) {
        parts.push(`  Tag: <${ctx.tagName}>`);
      }
    } else {
      parts.push(`[${item.type}: ${item.label}]`);
    }
  }
  
  return parts.join('\n');
}


